import { ReactElement, useMemo, useRef, useState, MouseEvent } from 'react';
import { contours as d3Contours } from 'd3';
import {
  CARD_CLASS,
  CONC_UNIT,
  OPC_BIN_COUNT,
  binRangeLabel,
  fmtBoundary,
  fmtConc,
  fmtTimeTick,
  toggleClass,
  useElementWidth,
  binColor,
  sliceColor
} from './particleBinShared';

/**
 * Top margin has to clear what is drawn above the plot area: the slice panel's
 * caption and the numbered badges on each selection marker, both of which sit at
 * negative y. At 10px their ascenders were clipped by the SVG edge.
 */
const MARGIN = { top: 26, right: 74, bottom: 40, left: 56 };
const PLOT_HEIGHT = 320;
const LEVELS = 11;
/** Widest dynamic range the log colour ramp will span, in decades. */
const LOG_DECADES = 4;
/** Width of the click-to-slice profile panel that shares the y axis. */
const SLICE_W = 150;

/**
 * Default y window, for the OPC-N3: the fine bins carry the signal and the coarse
 * bins are ~always 0. Instruments with a different size range (the APS reaches
 * 20 µm across 52 channels) pass their own.
 */
const DEFAULT_Y_MIN_UM = 0.35;
const DEFAULT_Y_MAX_UM = 10;
const DEFAULT_Y_TICKS_UM = [0.35, 0.5, 1, 2, 5, 10];

type HoverInfo = { t: number; b: number; x: number; y: number };

type BinContourPlotProps = {
  timestamps: number[];
  /** Cell [t][b]; null = no data (treated as 0 for contouring). */
  values: (number | null)[][];
  boundaries: number[];
  /** Clicked time-bucket indexes (owned by the page so other charts can share them). */
  sliceTs: number[];
  /** Called with the next selection after a click or a chip dismissal. */
  onSlicesChange: (next: number[]) => void;
  /** Channels in the matrix. Defaults to the OPC-N3's 24. */
  binCount?: number;
  /** Unit label for the plotted quantity. Defaults to the OPC-N3's #/ml. */
  unit?: string;
  /** Log size axis bounds in µm, and the ticks drawn on it. */
  yMinUm?: number;
  yMaxUm?: number;
  yTicksUm?: number[];
  /** Card heading and the line under it. */
  title?: string;
  subtitle?: string;
};

/**
 * Pixel y for a particle size on the log axis.
 * @param um - Particle size in µm.
 * @param logMin - Log10 of the axis minimum.
 * @param logMax - Log10 of the axis maximum.
 * @returns Pixel y within the plot.
 */
function sizeToY(um: number, logMin: number, logMax: number): number {
  return PLOT_HEIGHT - ((Math.log10(um) - logMin) / (logMax - logMin)) * PLOT_HEIGHT;
}

/**
 * Serialize a d3-contour MultiPolygon into an SVG path, mapping each grid
 * coordinate to pixels: x linearly over time, y through the bin boundaries
 * onto the log size axis.
 * @param coordinates - MultiPolygon coordinate array in grid space.
 * @param mapX - Grid x → pixel x.
 * @param mapY - Grid y → pixel y.
 * @returns SVG path data.
 */
function contourPath(
  coordinates: number[][][][],
  mapX: (x: number) => number,
  mapY: (y: number) => number
): string {
  let d = '';
  coordinates.forEach((polygon) => {
    polygon.forEach((ring) => {
      ring.forEach(([x, y], i) => {
        d += `${i === 0 ? 'M' : 'L'}${mapX(x).toFixed(1)},${mapY(y).toFixed(1)}`;
      });
      d += 'Z';
    });
  });
  return d;
}

/**
 * Filled contour plot of the time × bin matrix with a log particle-size y axis
 * fixed to 0.35–10 µm (the size range where the OPC-N3 actually sees particles).
 * Clicking a time overlays that bucket's size distribution as a profile panel
 * on the shared size axis; clicking the same time again clears it.
 * @param props - Matrix data.
 * @returns Contour plot card.
 */
export default function BinContourPlot(props: BinContourPlotProps): ReactElement {
  const {
    timestamps,
    values,
    boundaries,
    sliceTs,
    onSlicesChange,
    binCount = OPC_BIN_COUNT,
    unit = CONC_UNIT,
    yMinUm = DEFAULT_Y_MIN_UM,
    yMaxUm = DEFAULT_Y_MAX_UM,
    yTicksUm = DEFAULT_Y_TICKS_UM,
    title = 'Concentration Contours',
    subtitle
  } = props;
  const logYMin = Math.log10(yMinUm);
  const logYMax = Math.log10(yMaxUm);
  /**
   * Pixel y for a size, with this chart's axis bounds bound in.
   * @param um - Particle size in µm.
   * @returns Pixel y.
   */
  const sizeToYAxis = (um: number): number => sizeToY(um, logYMin, logYMax);
  const [logScale, setLogScale] = useState(true);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(containerRef);

  const nT = timestamps.length;
  // A refetch renumbers buckets, so ignore anything now out of range.
  const activeSlices = useMemo(
    () => sliceTs.filter((t) => t >= 0 && t < nT).sort((a, b) => a - b),
    [sliceTs, nT]
  );
  const hasSlice = activeSlices.length > 0;
  const rightMargin = MARGIN.right + (hasSlice ? SLICE_W : 0);
  const plotW = Math.max(width - MARGIN.left - rightMargin, 50);
  const height = PLOT_HEIGHT + MARGIN.top + MARGIN.bottom;
  const spanMs = nT > 1 ? timestamps[nT - 1] - timestamps[0] : 0;

  // Contours are computed once in grid space; only the mapping depends on width.
  //
  // The grid is normalised to 0..1 before contouring, so the thresholds are simply
  // even fractions and `frac` is already the position on the colour ramp. The log
  // mode is a true log10 ramp between a floor and the maximum — an earlier log1p
  // version was very nearly a no-op, because log1p(x) ≈ x for the sub-1 values
  // that make up the whole of a typical OPC matrix.
  const { contourList, maxValue, denorm } = useMemo(() => {
    /**
     * Identity fallback used when there is nothing to draw.
     * @param f - Normalised 0..1.
     * @returns The same value.
     */
    const noop = (f: number): number => f;
    if (nT === 0) {
      return {
        contourList: [] as { coords: number[][][][]; frac: number; value: number }[],
        maxValue: 0,
        denorm: noop
      };
    }
    let max = 0;
    let minPositive = Infinity;
    for (let b = 0; b < binCount; b += 1) {
      for (let t = 0; t < nT; t += 1) {
        const v = values[t]?.[b] ?? null;
        if (v !== null && Number.isFinite(v)) {
          if (v > max) max = v;
          if (v > 0 && v < minPositive) minPositive = v;
        }
      }
    }
    if (max <= 0) return { contourList: [], maxValue: max, denorm: noop };

    // Floor of the log ramp. Anchored on the smallest positive value so the fine
    // bins actually separate, but never more than LOG_DECADES below the maximum —
    // otherwise one near-zero outlier stretches the ramp until everything real
    // crowds into the top colour.
    const floor = Math.max(
      Number.isFinite(minPositive) ? minPositive : max / 10 ** LOG_DECADES,
      max / 10 ** LOG_DECADES
    );
    const logFloor = Math.log10(floor);
    const logMax = Math.log10(max);
    const logSpan = logMax - logFloor;
    const useLog = logScale && logSpan > 0;

    /**
     * Concentration → position on the colour ramp.
     * @param v - Concentration.
     * @returns Normalised 0..1.
     */
    const norm = (v: number): number => {
      if (!useLog) return v / max;
      if (v <= floor) return 0;
      return (Math.log10(v) - logFloor) / logSpan;
    };

    /**
     * Ramp position → concentration, for the colour-bar labels.
     * @param f - Normalised 0..1.
     * @returns Concentration in real units.
     */
    const toValue = (f: number): number =>
      useLog ? 10 ** (logFloor + f * logSpan) : f * max;

    // d3-contour wants a flat grid: grid[y * width + x], y = bin, x = time bucket.
    const grid = new Float64Array(nT * binCount);
    for (let b = 0; b < binCount; b += 1) {
      for (let t = 0; t < nT; t += 1) {
        const v = values[t]?.[b] ?? null;
        grid[b * nT + t] = norm(v === null || v < 0 ? 0 : v);
      }
    }

    // Levels start above zero so the empty background stays unfilled.
    const thresholds = Array.from({ length: LEVELS }, (_, i) => (i + 0.5) / LEVELS);
    const generator = d3Contours().size([nT, binCount]).thresholds(thresholds);
    const list = generator(Array.from(grid)).map((c) => ({
      coords: c.coordinates as unknown as number[][][][],
      frac: c.value,
      value: toValue(c.value)
    }));
    return { contourList: list, maxValue: max, denorm: toValue };
  }, [values, nT, logScale, binCount]);

  /**
   * Grid y (continuous bin index 0..24) → pixel y on the log size axis,
   * interpolating log-linearly between the bin boundaries.
   * @param g - Grid y coordinate.
   * @returns Pixel y.
   */
  const gridToY = (g: number): number => {
    const i = Math.min(Math.max(Math.floor(g), 0), binCount - 1);
    const frac = g - i;
    const lo = Math.log10(boundaries[i]);
    const hi = Math.log10(boundaries[i + 1]);
    const logSize = lo + frac * (hi - lo);
    return PLOT_HEIGHT - ((logSize - logYMin) / (logYMax - logYMin)) * PLOT_HEIGHT;
  };

  const paths = useMemo(
    () =>
      contourList.map((c) => ({
        d: contourPath(c.coords, (x) => (x / nT) * plotW, gridToY),
        frac: c.frac,
        value: c.value
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contourList, nT, plotW, boundaries]
  );

  /**
   * Pointer → grid cell: x is linear over buckets, y inverts the log size
   * axis and finds the bin whose boundaries contain that size.
   * @param e - Mouse event over the SVG.
   * @returns The cell under the pointer, or null when outside the plot.
   */
  const pointToCell = (e: MouseEvent<SVGSVGElement>): HoverInfo | null => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left - MARGIN.left;
    const py = e.clientY - rect.top - MARGIN.top;
    if (px < 0 || py < 0 || px >= plotW || py >= PLOT_HEIGHT || nT === 0) {
      return null;
    }
    const size = 10 ** (logYMax - (py / PLOT_HEIGHT) * (logYMax - logYMin));
    let b = -1;
    for (let i = 0; i < binCount; i += 1) {
      if (size >= boundaries[i] && size < boundaries[i + 1]) {
        b = i;
        break;
      }
    }
    if (b < 0) return null;
    const t = Math.min(Math.floor((px / plotW) * nT), nT - 1);
    return { t, b, x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const hoverValue = hover ? values[hover.t]?.[hover.b] ?? null : null;

  // One profile per picked time, all sharing the log size axis and — so they can
  // be read against each other — a single x scale spanning every selection.
  const { profiles, sliceMax, sliceMin } = useMemo(() => {
    let max = 0;
    let minPositive = Infinity;
    const built = activeSlices.map((t) => {
      const row = values[t] ?? [];
      const points: { b: number; v: number; y: number }[] = [];
      for (let b = 0; b < binCount; b += 1) {
        const lo = boundaries[b];
        const hi = boundaries[b + 1];
        const v = row[b];
        const inWindow = hi > yMinUm && lo < yMaxUm;
        if (inWindow && v !== null && v !== undefined) {
          const center = Math.sqrt(lo * hi);
          points.push({ b, v, y: sizeToYAxis(Math.min(Math.max(center, yMinUm), yMaxUm)) });
          if (v > max) max = v;
          if (v > 0 && v < minPositive) minPositive = v;
        }
      }
      return { t, points };
    });
    return {
      profiles: built,
      sliceMax: max,
      sliceMin: Number.isFinite(minPositive) ? minPositive : max
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlices, values, boundaries, binCount, yMinUm, yMaxUm, logYMin, logYMax]);

  /**
   * Concentration → x offset inside the slice panel, following the same
   * log/linear toggle as the contour levels.
   * @param v - Concentration.
   * @returns Offset 0..panel width.
   */
  const sliceX = (v: number): number => {
    if (sliceMax <= 0) return 0;
    const usable = SLICE_W - 34;
    const floor = Math.max(sliceMin, sliceMax / 10 ** LOG_DECADES);
    const logSpan = Math.log10(sliceMax) - Math.log10(floor);
    let frac;
    if (logScale && logSpan > 0) {
      frac = v <= floor ? 0 : (Math.log10(v) - Math.log10(floor)) / logSpan;
    } else {
      frac = v / sliceMax;
    }
    return Math.max(0, Math.min(frac, 1)) * usable;
  };

  /**
   * Position of a picked time along the x axis.
   * @param t - Bucket index.
   * @returns Pixel x at the centre of that bucket.
   */
  const bucketX = (t: number): number => ((t + 0.5) / nT) * plotW;

  /**
   * Add or remove a time from the selection, keeping chronological order.
   * @param t - Bucket index that was clicked.
   */
  const toggleSlice = (t: number): void => {
    const next = activeSlices.includes(t)
      ? activeSlices.filter((x) => x !== t)
      : [...activeSlices, t].sort((a, b) => a - b);
    onSlicesChange(next);
  };

  const tickIdx = useMemo(() => {
    if (nT === 0) return [] as number[];
    const count = Math.min(6, nT);
    return Array.from({ length: count }, (_, i) => Math.floor((i * (nT - 1)) / Math.max(count - 1, 1)));
  }, [nT]);

  /**
   * Path through one profile's points.
   * @param points - Profile points.
   * @returns SVG path data, empty when there is nothing to draw.
   */
  const profilePath = (points: { v: number; y: number }[]): string =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${sliceX(p.v).toFixed(1)},${p.y.toFixed(1)}`).join('');

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-100">{title}</h2>
          <p className="text-xs text-gray-400">
            {subtitle ??
              `Time × particle size (${yMinUm}–${yMaxUm} µm, log axis); filled contours of concentration (${unit})`}{' '}
            · click times to compare their size distributions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasSlice && (
            <button type="button" className={toggleClass(false)} onClick={() => onSlicesChange([])}>
              Clear all
            </button>
          )}
          <button type="button" className={toggleClass(logScale)} onClick={() => setLogScale(!logScale)}>
            Log colour scale
          </button>
        </div>
      </div>
      {hasSlice && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {activeSlices.map((t, i) => (
            <span
              key={timestamps[t]}
              className="inline-flex items-center gap-1.5 rounded border border-gray-700 bg-background-700 py-0.5 pl-2 pr-1 text-[11px] text-gray-200"
            >
              <span
                className="inline-block h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: sliceColor(i) }}
              />
              {new Date(timestamps[t]).toLocaleString()}
              <button
                type="button"
                aria-label={`Remove ${new Date(timestamps[t]).toLocaleString()}`}
                onClick={() => onSlicesChange(activeSlices.filter((x) => x !== t))}
                className="flex h-4 w-4 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-700 hover:text-gray-200"
              >
                <span className="material-icons-outlined text-[12px] leading-none">close</span>
              </button>
            </span>
          ))}
        </div>
      )}
      <div ref={containerRef} className="relative w-full">
        {nT === 0 || maxValue <= 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            {nT === 0 ? 'No data in the selected range.' : 'All concentrations are zero in this range.'}
          </p>
        ) : (
          width > 0 && (
            <svg
              width={width}
              height={height}
              className="cursor-crosshair"
              onMouseMove={(e) => setHover(pointToCell(e))}
              onMouseLeave={() => setHover(null)}
              onClick={(e) => {
                const cell = pointToCell(e);
                if (!cell) return;
                toggleSlice(cell.t);
              }}
              role="img"
              aria-label="OPC bin concentration contour plot"
            >
              <defs>
                <linearGradient id="opc-contour-legend" x1="0" y1="1" x2="0" y2="0">
                  {Array.from({ length: 13 }, (_, i) => (
                    <stop key={i} offset={`${(i / 12) * 100}%`} stopColor={binColor(i / 12)} />
                  ))}
                </linearGradient>
                <clipPath id="opc-contour-clip">
                  <rect width={plotW} height={PLOT_HEIGHT} />
                </clipPath>
              </defs>
              <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                <rect width={plotW} height={PLOT_HEIGHT} fill="#111827" />
                <g clipPath="url(#opc-contour-clip)">
                  {paths.map((p) => (
                    <path
                      key={p.frac}
                      d={p.d}
                      fill={binColor(p.frac)}
                      stroke="rgba(17,24,39,0.55)"
                      strokeWidth={0.8}
                    />
                  ))}
                </g>
                {/* Y axis: log particle-size ticks */}
                {yTicksUm.map((um) => (
                  <g key={um} transform={`translate(0,${sizeToYAxis(um)})`}>
                    <line x2={plotW} stroke="rgba(156,163,175,0.12)" />
                    <line x2={-4} stroke="#4b5563" />
                    <text x={-8} y={3} textAnchor="end" fontSize={9} fill="#9ca3af">
                      {fmtBoundary(um)}
                    </text>
                  </g>
                ))}
                <text
                  transform={`translate(${-MARGIN.left + 12},${PLOT_HEIGHT / 2}) rotate(-90)`}
                  textAnchor="middle"
                  fontSize={11}
                  fill="#9ca3af"
                >
                  Particle size (µm)
                </text>
                {/* X axis ticks */}
                {tickIdx.map((i) => (
                  <g
                    key={i}
                    transform={`translate(${((i + 0.5) / nT) * plotW},${PLOT_HEIGHT})`}
                  >
                    <line y2={4} stroke="#4b5563" />
                    <text y={16} textAnchor="middle" fontSize={10} fill="#9ca3af">
                      {fmtTimeTick(timestamps[i], spanMs)}
                    </text>
                  </g>
                ))}
                {/* Clicked-time markers, one per selection */}
                {activeSlices.map((t, i) => (
                  <g key={timestamps[t]} transform={`translate(${bucketX(t)},0)`}>
                    <line
                      y2={PLOT_HEIGHT}
                      stroke={sliceColor(i)}
                      strokeWidth={1.2}
                      strokeDasharray="4 3"
                      opacity={0.9}
                    />
                    <circle cy={-1} r={5.5} fill={sliceColor(i)} />
                    <text
                      y={2}
                      textAnchor="middle"
                      fontSize={7.5}
                      fontWeight="700"
                      fill="#111827"
                    >
                      {i + 1}
                    </text>
                  </g>
                ))}
                {/* Legend */}
                <g transform={`translate(${plotW + 16},0)`}>
                  <rect width={12} height={PLOT_HEIGHT} fill="url(#opc-contour-legend)" rx={2} />
                  {[0, 0.5, 1].map((f) => (
                    <text key={f} x={16} y={PLOT_HEIGHT - f * PLOT_HEIGHT + 3} fontSize={9} fill="#9ca3af">
                      {fmtConc(denorm(f))}
                    </text>
                  ))}
                </g>
                {/* Slice profile panel: the size distribution at each picked time,
                    sharing this chart's size axis and one common value scale. */}
                {hasSlice && (
                  <g transform={`translate(${plotW + MARGIN.right - 8},0)`}>
                    <rect
                      x={0}
                      y={-2}
                      width={SLICE_W}
                      height={PLOT_HEIGHT + 4}
                      fill="#0b1220"
                      stroke="#374151"
                      rx={3}
                    />
                    <text x={0} y={-9} fontSize={9} fill="#e5e7eb">
                      {activeSlices.length === 1
                        ? fmtTimeTick(timestamps[activeSlices[0]], spanMs)
                        : `${activeSlices.length} times selected`}
                    </text>
                    {sliceMax <= 0 ? (
                      <text
                        x={SLICE_W / 2}
                        y={PLOT_HEIGHT / 2}
                        textAnchor="middle"
                        fontSize={9}
                        fill="#6b7280"
                      >
                        no data
                      </text>
                    ) : (
                      <g transform="translate(8,0)">
                        {/* Faint rails tie the single-selection profile to the size rows;
                            with several profiles they would just be clutter. */}
                        {profiles.length === 1 &&
                          profiles[0].points.map((p) => (
                            <line
                              key={p.b}
                              x1={0}
                              x2={sliceX(p.v)}
                              y1={p.y}
                              y2={p.y}
                              stroke="rgba(156,163,175,0.18)"
                            />
                          ))}
                        {profiles.map((profile, i) => (
                          <g key={timestamps[profile.t]}>
                            <path
                              d={profilePath(profile.points)}
                              fill="none"
                              stroke={sliceColor(i)}
                              strokeWidth={1.4}
                              opacity={0.95}
                            />
                            {profile.points.map((p) => (
                              <circle
                                key={p.b}
                                cx={sliceX(p.v)}
                                cy={p.y}
                                r={1.9}
                                fill={sliceColor(i)}
                              >
                                <title>
                                  {`${new Date(timestamps[profile.t]).toLocaleString()} · ${binRangeLabel(
                                    boundaries,
                                    p.b
                                  )} µm: ${fmtConc(p.v)} ${unit}`}
                                </title>
                              </circle>
                            ))}
                          </g>
                        ))}
                        <line y1={0} y2={PLOT_HEIGHT} stroke="#4b5563" />
                        <text x={0} y={PLOT_HEIGHT + 12} fontSize={8.5} fill="#9ca3af">
                          0
                        </text>
                        <text
                          x={SLICE_W - 34}
                          y={PLOT_HEIGHT + 12}
                          textAnchor="end"
                          fontSize={8.5}
                          fill="#9ca3af"
                        >
                          {fmtConc(sliceMax)} {unit}
                        </text>
                      </g>
                    )}
                  </g>
                )}
              </g>
            </svg>
          )
        )}
        {hover && (
          <div
            className="pointer-events-none absolute z-10 rounded border border-gray-700 bg-background-700 px-2.5 py-1.5 text-xs text-gray-100 shadow-lg"
            style={{
              left: Math.min(hover.x + 12, Math.max(width - 190, 0)),
              top: Math.max(hover.y - 56, 0)
            }}
          >
            <div className="text-gray-400">{new Date(timestamps[hover.t]).toLocaleString()}</div>
            <div>
              Bin {hover.b} · {binRangeLabel(boundaries, hover.b)} µm
            </div>
            <div className="font-semibold">
              {hoverValue === null ? 'no data' : `${fmtConc(hoverValue)} ${unit}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
